import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {toLocalIso} from '../calendar/CalendarModel'
import {cancelInterview, completeInterview} from './Api'
import {IInterviewModel, interviewTimeRange} from './InterviewModel'

export type InterviewStatusAction = 'complete' | 'cancel'

type Props = {
  interview: IInterviewModel | null
  action: InterviewStatusAction
  onClose: () => void
  onSaved: () => void
}

/** "Gerçekleşti" (gerçekleşme zamanı + not) ve "İptal" (sebep) pop-up'ı; sonuç adayın künyesine işlenir */
export const InterviewStatusModal: FC<Props> = ({interview, action, onClose, onSaved}) => {
  const [completedAt, setCompletedAt] = useState('')
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const isCancel = action === 'cancel'

  useEffect(() => {
    if (!interview) return
    setText('')
    // Varsayılan gerçekleşme: planlanan başlangıç geçmişteyse o, değilse şimdi
    const planned = interview.start.substring(0, 16)
    const now = toLocalIso(new Date()).substring(0, 16)
    setCompletedAt(planned < now ? planned : now)
  }, [interview])

  const handleSave = async () => {
    if (!interview) return
    if (isCancel && !text.trim()) {
      showErrorToast('İptal sebebi zorunludur')
      return
    }
    setSaving(true)
    try {
      if (isCancel) {
        await cancelInterview(interview.id, text.trim())
        showSuccessToast('Mülakat iptal edildi')
      } else {
        await completeInterview(interview.id, completedAt ? `${completedAt}:00` : null, text.trim() || null)
        showSuccessToast('Mülakat gerçekleşti olarak işaretlendi')
      }
      onSaved()
      onClose()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Kaydedilemedi'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={!!interview} onHide={onClose} dialogClassName='modal-dialog-centered'>
      <div className='modal-header'>
        <div>
          <h5 className='modal-title'>{isCancel ? 'Mülakatı İptal Et' : 'Mülakat Gerçekleşti'}</h5>
          {interview && (
            <div className='text-muted fs-7'>
              {interview.candidateName} · {interviewTimeRange(interview)}
            </div>
          )}
        </div>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>
      <div className='modal-body'>
        {!isCancel && (
          <div className='mb-5'>
            <label className='form-label fw-bold'>Gerçekleşme Tarihi</label>
            <input
              type='datetime-local'
              className='form-control'
              value={completedAt}
              onChange={(e) => setCompletedAt(e.target.value)}
            />
          </div>
        )}
        <label className={`form-label fw-bold ${isCancel ? 'required' : ''}`}>{isCancel ? 'İptal Sebebi' : 'Not'}</label>
        <textarea
          rows={4}
          maxLength={isCancel ? 1000 : 5000}
          className='form-control'
          placeholder={isCancel ? 'Örn: Aday katılamayacağını bildirdi' : 'İsteğe bağlı mülakat notu'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {isCancel && (
          <div className='form-text'>Takvimdeki kayıt ve varsa Google Meet etkinliği silinir. Katılımcılara mail gönderilmez.</div>
        )}
      </div>
      <div className='modal-footer'>
        <button type='button' className='btn btn-light' onClick={onClose}>
          Vazgeç
        </button>
        <button
          type='button'
          className={`btn ${isCancel ? 'btn-danger' : 'btn-success'}`}
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? <span className='spinner-border spinner-border-sm' /> : isCancel ? 'İptal Et' : 'Kaydet'}
        </button>
      </div>
    </Modal>
  )
}
